import React, { useState, useEffect } from "react";
import {
    Box, Typography, TextField, MenuItem, Grid, ToggleButton, ToggleButtonGroup,
    Stack, Chip, Divider
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";

const FREQUENCIES = [
    { label: "Quotidien", value: "daily" },
    { label: "Hebdomadaire", value: "weekly" },
    { label: "Mensuel", value: "monthly" },
    { label: "Annuel", value: "yearly" },
    { label: "Horaire", value: "hourly" },
];

const MONTHS = [
    { label: "Janvier", value: "1" },
    { label: "Février", value: "2" },
    { label: "Mars", value: "3" },
    { label: "Avril", value: "4" },
    { label: "Mai", value: "5" },
    { label: "Juin", value: "6" },
    { label: "Juillet", value: "7" },
    { label: "Août", value: "8" },
    { label: "Septembre", value: "9" },
    { label: "Octobre", value: "10" },
    { label: "Novembre", value: "11" },
    { label: "Décembre", value: "12" },
];

const DAYS_OF_WEEK = [
    { label: "Lun", value: "1" },
    { label: "Mar", value: "2" },
    { label: "Mer", value: "3" },
    { label: "Jeu", value: "4" },
    { label: "Ven", value: "5" },
    { label: "Sam", value: "6" },
    { label: "Dim", value: "0" },
];

/**
 * CronEditor
 * A user-friendly component to generate a CRON expression.
 * 
 * Props:
 *  value: current CRON string
 *  onChange: (cronString) => void
 */
const CronEditor = ({ value, onChange }) => {
    // Internal state to manage the UI parts
    const [frequency, setFrequency] = useState("daily");
    const [hour, setHour] = useState("09");
    const [minute, setMinute] = useState("00");
    const [dayOfWeek, setDayOfWeek] = useState("1"); // Monday
    const [dayOfMonth, setDayOfMonth] = useState("1");
    const [month, setMonth] = useState("1"); // January (for yearly)
    const [isInternalChange, setIsInternalChange] = useState(false);

    // Parse the incoming CRON string to initialize the UI
    useEffect(() => {
        if (!value || isInternalChange) {
            setIsInternalChange(false);
            return;
        }

        const parts = value.split(" ");
        if (parts.length !== 5) return;

        const [m, h, dom, mon, dow] = parts;

        // Simple heuristic to detect frequency
        if (dow !== "*" && dow !== "?") {
            setFrequency("weekly");
            setDayOfWeek(dow);
            setHour(h.padStart(2, "0"));
            setMinute(m.padStart(2, "0"));
        } else if (mon !== "*" && mon !== "?" && dom !== "*" && dom !== "?") {
            // Specific month AND specific day → yearly
            setFrequency("yearly");
            setMonth(mon);
            setDayOfMonth(dom);
            setHour(h.padStart(2, "0"));
            setMinute(m.padStart(2, "0"));
        } else if (dom !== "*" && dom !== "?") {
            setFrequency("monthly");
            setDayOfMonth(dom);
            setHour(h.padStart(2, "0"));
            setMinute(m.padStart(2, "0"));
        } else if (h === "*") {
            setFrequency("hourly");
            setMinute(m.padStart(2, "0"));
        } else {
            setFrequency("daily");
            setHour(h.padStart(2, "0"));
            setMinute(m.padStart(2, "0"));
        }
    }, [value]);

    // Build the CRON string whenever internal state changes
    useEffect(() => {
        let h = hour;
        let m = minute;
        let dom = "*";
        let dow = "*";
        let mon = "*";

        if (frequency === "daily") {
            // cron: m h * * *
        } else if (frequency === "weekly") {
            dow = dayOfWeek;
        } else if (frequency === "monthly") {
            dom = dayOfMonth;
        } else if (frequency === "yearly") {
            dom = dayOfMonth;
            mon = month;
        } else if (frequency === "hourly") {
            h = "*";
        }

        const cron = `${parseInt(m, 10)} ${h === "*" ? "*" : parseInt(h, 10)} ${dom} ${mon} ${dow}`;
        if (cron !== value) {
            setIsInternalChange(true);
            onChange(cron);
        }
    }, [frequency, hour, minute, dayOfWeek, dayOfMonth, month]);

    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

    return (
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <ScheduleIcon sx={{ fontSize: 18, color: "#0369a1" }} />
                <Typography fontSize={13} fontWeight={700} color="#334155">
                    Planification simplifiée
                </Typography>
                <Chip
                    label={value || "* * * * *"}
                    size="small"
                    sx={{ ml: "auto", fontFamily: "monospace", fontSize: 11, bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 600 }}
                />
            </Box>

            <Grid container spacing={2}>
                {/* Frequency Selector */}
                <Grid item xs={12}>
                    <ToggleButtonGroup
                        value={frequency}
                        exclusive
                        onChange={(_, v) => v && setFrequency(v)}
                        size="small"
                        fullWidth
                        sx={{ mb: 1 }}
                    >
                        {FREQUENCIES.map((f) => (
                            <ToggleButton key={f.value} value={f.value} sx={{ textTransform: "none", fontSize: 12, fontWeight: 600 }}>
                                {f.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Grid>

                {/* Conditional Inputs */}
                {frequency === "weekly" && (
                    <Grid item xs={12}>
                        <Typography fontSize={11} color="text.secondary" mb={0.5}>Jour de la semaine :</Typography>
                        <ToggleButtonGroup
                            value={dayOfWeek}
                            exclusive
                            onChange={(_, v) => v && setDayOfWeek(v)}
                            size="small"
                            fullWidth
                        >
                            {DAYS_OF_WEEK.map((d) => (
                                <ToggleButton key={d.value} value={d.value} sx={{ fontSize: 11, px: 1 }}>
                                    {d.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Grid>
                )}

                {(frequency === "monthly" || frequency === "yearly") && (
                    <Grid item xs={12} sm={frequency === "yearly" ? 4 : 6}>
                        <TextField
                            fullWidth
                            select
                            size="small"
                            label="Jour du mois"
                            value={dayOfMonth}
                            onChange={(e) => setDayOfMonth(e.target.value)}
                            sx={fieldSx}
                        >
                            {[...Array(31)].map((_, i) => (
                                <MenuItem key={i + 1} value={(i + 1).toString()}>
                                    Le {i + 1}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                )}

                {frequency === "yearly" && (
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            select
                            size="small"
                            label="Mois"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            sx={fieldSx}
                        >
                            {MONTHS.map((mo) => (
                                <MenuItem key={mo.value} value={mo.value}>
                                    {mo.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                )}

                {frequency !== "hourly" && (
                    <>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth
                                select
                                size="small"
                                label="Heure"
                                value={hour}
                                onChange={(e) => setHour(e.target.value)}
                                sx={fieldSx}
                            >
                                {[...Array(24)].map((_, i) => (
                                    <MenuItem key={i} value={i.toString().padStart(2, "0")}>
                                        {i.toString().padStart(2, "0")}h
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth
                                select
                                size="small"
                                label="Minute"
                                value={minute}
                                onChange={(e) => setMinute(e.target.value)}
                                sx={fieldSx}
                            >
                                {["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"].map((m) => (
                                    <MenuItem key={m} value={m}>
                                        {m}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </>
                )}

                {frequency === "hourly" && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            select
                            size="small"
                            label="Toutes les X minutes"
                            value={minute}
                            onChange={(e) => setMinute(e.target.value)}
                            sx={fieldSx}
                        >
                            {["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"].map((m) => (
                                <MenuItem key={m} value={m}>
                                    À la minute {m}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                )}
            </Grid>

            <Box mt={2} pt={1.5} sx={{ borderTop: "1px dashed #e2e8f0" }}>
                <Typography fontSize={11} color="text.secondary" fontStyle="italic">
                    💡 {frequency === "daily" && `Exécution quotidienne à ${hour}:${minute}`}
                    {frequency === "weekly" && `Exécution chaque ${DAYS_OF_WEEK.find(d => d.value === dayOfWeek).label} à ${hour}:${minute}`}
                    {frequency === "monthly" && `Exécution le ${dayOfMonth} de chaque mois à ${hour}:${minute}`}
                    {frequency === "yearly" && `Exécution le ${dayOfMonth} ${MONTHS.find(mo => mo.value === month)?.label} de chaque année à ${hour}:${minute}`}
                    {frequency === "hourly" && `Exécution chaque heure à la minute ${minute}`}
                </Typography>
            </Box>
        </Box>
    );
};

export default CronEditor;
